<script setup>
import { useRouter } from "vue-router";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useResourceList } from "@/composables/useResourceList";
import { formatDate } from "@/lib/date";
import { fetchUsers, deleteUser, resetUserPassword } from "./api";
import { downloadCsv } from "@/lib/csv";

const router = useRouter();
const columns = [
  { key: "name",        label: "Name",        sortable: true },
  { key: "display_name", label: "Display Name" },
  { key: "customer_id", label: "Employee No.", sortable: true },
  { key: "company",     label: "Company" },
  { key: "department",  label: "Department" },
  { key: "dob",         label: "DOB" },
  { key: "points",      label: "Points", align: "end", sortable: true },
  { key: "actions",     label: "Actions", align: "end", width: "220px" },
];

/** Resolve the canonical document id from whatever the backend returns. */
function resolveId(u) {
  return u._id ?? u.objectID ?? u.guid ?? u.id ?? u.user_id ?? null;
}

const { rows, loading, error, pendingDelete, deleting, load, askDelete, confirmDelete } =
  useResourceList({
    fetch: fetchUsers,
    remove: deleteUser,
    mapRow: (u) => ({
      id: resolveId(u),
      name: u.name, display_name: u.display_name,
      customer_id: u.customer_id, company: u.company,
      department: u.department, dob: formatDate(u.dob), points: u.points,
    }),
  });
function exportCsv() { downloadCsv("users.csv", columns.filter((c) => c.key !== "actions"), rows.value); }

const resetingPwd = ref(false);
const resetPwdError = ref("");
const resetPwdSuccess = ref("");

async function resetPassword(row) {
  if (!row.customer_id) return;
  resetingPwd.value = true; resetPwdError.value = ""; resetPwdSuccess.value = "";
  try {
    await resetUserPassword(row.customer_id);
    resetPwdSuccess.value = `Password reset for ${row.name || 'user'}.`;
    setTimeout(() => { resetPwdSuccess.value = ""; }, 4000);
  } catch { resetPwdError.value = "Couldn't reset password."; }
  finally { resetingPwd.value = false; }
}

function editUser(row) {
  if (!row.id) { console.warn('[Users] row has no id — raw row:', row); return; }
  router.push({ name: 'users-edit', params: { id: row.id } });
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Manage Users</h1>
      <div class="d-flex gap-2 flex-wrap">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn variant="tonal" @click="exportCsv">Export CSV</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'users-points' })">Batch Points</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'users-batch' })">Batch Users</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'users-apple-links' })">Batch Add Apple Links</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'users-emergenetics' })">Batch Upload Emergenetics</VBtn>
        <VBtn variant="tonal" @click="router.push({ name: 'users-reset-password' })">Reset Password</VBtn>
        <VBtn @click="router.push({ name: 'users-new' })">Add User</VBtn>
      </div>
    </div>
    <VAlert v-if="resetPwdSuccess" type="success" closable class="mb-4" @click:close="resetPwdSuccess = ''">{{ resetPwdSuccess }}</VAlert>
    <VAlert v-if="resetPwdError" type="error" closable class="mb-4" @click:close="resetPwdError = ''">{{ resetPwdError }}</VAlert>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search users…" empty-text="No users found.">
      <template #cell-actions="{ row }">
        <VBtn size="small" variant="text" @click.stop="editUser(row)">Edit</VBtn>
        <VBtn size="small" variant="text" :loading="resetingPwd" @click.stop="resetPassword(row)">Reset Pwd</VBtn>
        <VBtn size="small" color="error" variant="text" @click.stop="askDelete(row)">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete user?" :message="`Permanently delete ${pendingDelete?.name || 'this user'}?`" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />
  </section>
</template>
